// Represents all the necessary info to render the items in the Create Campaign menu.

import {
  campaignTypeNameMap,
  MAX_CAMPAIGN_NAME_LENGTH,
} from 'common/src/modules/campaigns/campaigns.consts';
import {EntityRenameConfig} from 'common/src/modules/shared/copilot/copilot.types';
import {FeatureFlagKey} from '../featureFlags/featureFlags.types';
import isFlagEnabled from '../featureFlags/utils/isFlagEnabled';
import {
  CampaignType,
  CampaignTypeConfig,
  CampaignTypeName,
  SocialCampaignSubType,
  SocialCampaignSubTypeName,
  SocialCampaignSubTypeTitle,
} from './campaigns.types';
import {renameCampaigns} from './store/campaignsLibrary/thunks/renameCampaigns';

// Contains information per campaign type and its subtypes, if any.
const campaignTypeConfigs: CampaignTypeConfig[] = [
  {
    type: CampaignTypeName.DynamicBanners,
    id: CampaignType.DynamicBanners,
    label: campaignTypeNameMap[CampaignType.DynamicBanners],
    isSocial: false,
    isDuplicationAllowed: true,
    testIsEnabled: () => {
      return !isFlagEnabled(FeatureFlagKey.DisableDisplayFCCampaigns);
    },
    subtypes: [],
  },
  {
    type: CampaignTypeName.InBannerVideoAds,
    id: CampaignType.InBannerVideoAds,
    label: campaignTypeNameMap[CampaignType.InBannerVideoAds],
    isSocial: false,
    isDuplicationAllowed: true,
    testIsEnabled: () => {
      return !isFlagEnabled(FeatureFlagKey.DisableInBannerVideoFCCampaigns);
    },
    subtypes: [],
  },
  {
    type: CampaignTypeName.VideoAds,
    id: CampaignType.VideoAds,
    label: campaignTypeNameMap[CampaignType.VideoAds],
    isSocial: false,
    isDuplicationAllowed: true,
    testIsEnabled: () => {
      return !isFlagEnabled(FeatureFlagKey.DisableVideoFCCampaigns);
    },
    subtypes: [],
  },
  {
    type: CampaignTypeName.InteractiveVideoAds,
    id: CampaignType.InteractiveVideoAds,
    label: campaignTypeNameMap[CampaignType.InteractiveVideoAds],
    isSocial: false,
    isDuplicationAllowed: true,
    testIsEnabled: () => {
      return !isFlagEnabled(FeatureFlagKey.DisableInteractiveVideoFCCampaigns);
    },
    subtypes: [],
  },
  {
    type: CampaignTypeName.ThirdPartyAdTracking,
    id: CampaignType.ThirdPartyAdTracking,
    label: campaignTypeNameMap[CampaignType.ThirdPartyAdTracking],
    isSocial: false,
    isDuplicationAllowed: false,
    testIsEnabled: () => {
      return !isFlagEnabled(FeatureFlagKey.DisableThirdPartyAdTrackingCampaigns);
    },
    subtypes: [],
  },
  {
    type: CampaignTypeName.FacebookVideoAds,
    id: CampaignType.FacebookVideoAds,
    label: campaignTypeNameMap[CampaignType.FacebookVideoAds],
    isSocial: true,
    isDuplicationAllowed: false,
    testIsEnabled: () => {
      return !isFlagEnabled(FeatureFlagKey.DisableFacebookFCCampaigns);
    },
    subtypes: [
      {
        type: SocialCampaignSubTypeName.FacebookStandard,
        label: `${SocialCampaignSubTypeTitle.FacebookStandard} Campaign`,
        id: SocialCampaignSubType.FacebookStandard,
        testIsEnabled: () => true,
      },
      {
        type: SocialCampaignSubTypeName.FacebookDPA,
        label: `${SocialCampaignSubTypeTitle.FacebookDPA} Campaign`,
        id: SocialCampaignSubType.FacebookDPA,
        testIsEnabled: () => true,
      },
    ],
  },
  {
    type: CampaignTypeName.TikTokAds,
    id: CampaignType.TikTokAds,
    label: campaignTypeNameMap[CampaignType.TikTokAds],
    isSocial: true,
    isDuplicationAllowed: true,
    testIsEnabled: () => {
      return !isFlagEnabled(FeatureFlagKey.DisableTikTokFCCampaigns);
    },
    subtypes: [
      {
        type: SocialCampaignSubTypeName.TiktokStandard,
        label: 'Standard',
        id: SocialCampaignSubType.TiktokStandard,
        testIsEnabled: () => {
          return true;
        },
      },
    ],
  },
  {
    type: CampaignTypeName.YoutubeVideoAds,
    id: CampaignType.YoutubeVideoAds,
    label: campaignTypeNameMap[CampaignType.YoutubeVideoAds],
    isSocial: true,
    isDuplicationAllowed: true,
    testIsEnabled: () => {
      return !isFlagEnabled(FeatureFlagKey.DisableYoutubeFCCampaigns);
    },
    subtypes: [
      {
        type: SocialCampaignSubTypeName.YoutubeGoogleAds,
        label: SocialCampaignSubTypeTitle.GoogleAds,
        id: SocialCampaignSubType.YoutubeGoogleAds,
        testIsEnabled: () => {
          return true;
        },
      },
      {
        type: SocialCampaignSubTypeName.YoutubeDV360,
        label: SocialCampaignSubTypeTitle.DisplayAndVideo,
        id: SocialCampaignSubType.YoutubeDV360,
        testIsEnabled: () => {
          return isFlagEnabled(FeatureFlagKey.EnableYoutubeDV360Campaign);
        },
      },
    ],
  },
  {
    type: CampaignTypeName.SnapchatAds,
    id: CampaignType.SnapchatAds,
    label: 'Snapchat',
    isSocial: true,
    isDuplicationAllowed: true,
    testIsEnabled: () => {
      return true;
    },
    subtypes: [
      {
        type: SocialCampaignSubTypeName.SnapchatStandard,
        label: 'Standard',
        id: SocialCampaignSubType.SnapchatStandard,
        testIsEnabled: () => {
          return true;
        },
      },
    ],
  },
  {
    type: CampaignTypeName.LinkedInAds,
    id: CampaignType.LinkedInAds,
    label: 'LinkedIn',
    isSocial: true,
    isDuplicationAllowed: true,
    testIsEnabled: () => {
      return isFlagEnabled(FeatureFlagKey.EnableLinkedinCampaign);
    },
    subtypes: [
      {
        type: SocialCampaignSubTypeName.LinkedInStandard,
        label: 'Standard',
        id: SocialCampaignSubType.LinkedInStandard,
        testIsEnabled: () => {
          return true;
        },
      },
    ],
  },
  {
    type: CampaignTypeName.NativeAds,
    id: CampaignType.NativeAds,
    label: 'Native',
    isSocial: false,
    isDuplicationAllowed: true,
    testIsEnabled: () => {
      return !isFlagEnabled(FeatureFlagKey.DisableNativeFCCampaigns);
    },
    subtypes: [],
  },
  {
    type: CampaignTypeName.DoohImageAd,
    id: CampaignType.DoohImageAd,
    label: 'Digital Out-of-Home Image',
    isSocial: false,
    isDuplicationAllowed: true,
    testIsEnabled: () => {
      return !isFlagEnabled(FeatureFlagKey.DisableDigitalOutOfHomeImageCampaigns);
    },
    subtypes: [],
  },
  {
    type: CampaignTypeName.AudioAds,
    id: 15,
    label: 'Audio',
    isSocial: false,
    isDuplicationAllowed: true,
    testIsEnabled: () => {
      return !isFlagEnabled(FeatureFlagKey.DisableAudioAdsFCCampaigns);
    },
    subtypes: [],
  },
];

const campaignsRenameConfig: EntityRenameConfig = {
  maxNameLength: MAX_CAMPAIGN_NAME_LENGTH,
  thunkAction: renameCampaigns,
};

export {campaignsRenameConfig, campaignTypeConfigs};
